import { Button, Radio, Select, Tooltip, Typography } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import type { BidAssociation } from "design-scope";
import { Section } from "./scope-item-field";

export type ScopeBidPackagesEditorProps = {
  bids: BidAssociation[];
  bpOptions: Array<{ value: string; label: string }>;
  onChange: (bids: BidAssociation[]) => void;
  /** Overrides the empty-list helper text (e.g. bulk Mixed state). */
  emptyMessage?: string;
};

/** Bid-package list with add / primary / remove (shared by create/edit and bulk). */
export default function ScopeBidPackagesEditor(
  props: ScopeBidPackagesEditorProps
) {
  const { bids, bpOptions, onChange, emptyMessage } = props;

  const selectedBidIds = new Set(
    bids.map((b) => b.bidPackageId).filter(Boolean)
  );

  // Per-row options: hide packages already picked on other rows; keep this
  // row's current value so the Select still displays it.
  const bpOptionsForRow = (rowIndex: number) =>
    bpOptions.filter(
      (o) =>
        o.value === bids[rowIndex]?.bidPackageId || !selectedBidIds.has(o.value)
    );

  const canAddBid =
    bpOptions.length > 0 && selectedBidIds.size < bpOptions.length;

  const patchBid = (rowIndex: number, patch: Partial<BidAssociation>) => {
    onChange(bids.map((b, idx) => (idx === rowIndex ? { ...b, ...patch } : b)));
  };

  const removeBid = (rowIndex: number) => {
    onChange(bids.filter((_, idx) => idx !== rowIndex));
  };

  return (
    <Section
      title="Bid Packages"
      action={
        <Tooltip
          title={
            canAddBid
              ? undefined
              : bpOptions.length === 0
                ? "No Bid Packages available"
                : "All available Bid Packages are already added"
          }
        >
          <span>
            <Button
              icon={<PlusOutlined />}
              data-testid="bid-add"
              disabled={!canAddBid}
              onClick={() =>
                onChange([
                  ...bids,
                  {
                    bidPackageId: "",
                    role: "",
                    primary: false
                  }
                ])
              }
            >
              Add
            </Button>
          </span>
        </Tooltip>
      }
    >
      {bids.length > 0 && (
        <div className="dsm-grid dsm-grid-bids">
          <div className="dsm-grid-head">
            <span>Bid Package</span>
            <span className="text-center">Primary</span>
            <span />
          </div>
          {bids.map((b, i) => (
            <div key={i} className="dsm-grid-row">
              <Select
                showSearch
                // Type to filter — the "Show all" list runs long, so
                // scrolling alone isn't enough.
                optionFilterProp="label"
                data-testid={`bid-bp-${i}`}
                className="w-full"
                placeholder="Select Bid Package"
                value={b.bidPackageId || undefined}
                options={bpOptionsForRow(i)}
                onChange={(bidPackageId) => patchBid(i, { bidPackageId })}
              />
              {/* Shared `name` so the rows behave as one keyboard radio group. */}
              <Radio
                name="bid-primary"
                data-testid={`bid-primary-${i}`}
                className="justify-self-center"
                checked={b.primary}
                onChange={() =>
                  onChange(bids.map((x, idx) => ({ ...x, primary: idx === i })))
                }
              />
              <Button
                type="text"
                danger
                size="small"
                data-testid={`bid-remove-${i}`}
                icon={<DeleteOutlined />}
                onClick={() => removeBid(i)}
              />
            </div>
          ))}
        </div>
      )}
      {bids.length === 0 && (
        <Typography.Text type="secondary">
          {emptyMessage ?? "No Bid Packages yet."}
        </Typography.Text>
      )}
    </Section>
  );
}
